import { useEffect, useState } from 'react';
import { Icon } from '../icons';
import { ref, getDownloadURL } from 'firebase/storage';
import { getStorageOrThrow } from '../../lib/firebase';
import type { JobDoc } from '../../lib/types';

interface Props {
  job: JobDoc | null;
  onNext: () => void;
  onBack: () => void;
}

export function PreviewScreen({ job, onNext, onBack }: Props) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function urlWithRetries(storagePath: string): Promise<string> {
      const storage = getStorageOrThrow();
      const r = ref(storage, storagePath);
      const delaysMs = [0, 800, 2000, 4000];
      let lastErr: unknown;
      for (const d of delaysMs) {
        if (d) await new Promise((res) => setTimeout(res, d));
        if (cancelled) throw new Error('cancelled');
        try {
          return await getDownloadURL(r);
        } catch (e) {
          lastErr = e;
        }
      }
      throw lastErr;
    }
    async function load() {
      const previewPath = job?.assets?.previewPath;
      const thumbPath = job?.assets?.thumbPath;
      try {
        if (previewPath) {
          const url = await urlWithRetries(previewPath);
          if (!cancelled) setPreviewUrl(url);
        }
        if (thumbPath) {
          const url = await urlWithRetries(thumbPath);
          if (!cancelled) setThumbUrl(url);
        }
      } catch {
        // ignore — paths may not be readable yet, or Storage returned 5xx until auth/domain is fixed
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [job?.assets?.previewPath, job?.assets?.thumbPath]);

  const captionTeaser =
    job?.script?.captions?.[0]?.text ?? '"היום הגדול שלך הגיע..."';
  const durationLabel = job?.script?.durationSec
    ? formatDuration(job.script.durationSec)
    : '1:48';

  return (
    <div className="screen">
      <div className="screen-header">
        <button className="icon-btn" onClick={onBack}>
          <Icon.ChevronRight size={20} />
        </button>
        <div className="step-counter">תצוגה מקדימה</div>
        <button className="icon-btn">
          <Icon.More size={18} />
        </button>
      </div>
      <div className="screen-scroll">
        <div className="eyebrow" style={{ marginTop: 18 }}>
          הסרטון מוכן
        </div>
        <h1 className="h1" style={{ fontSize: 26 }}>
          נראה מצוין
        </h1>
        <p className="lede">בדקו את התצוגה המקדימה. הסרטון מסומן בסימן מים עד הרכישה.</p>

        <div className="video-preview">
          {previewUrl ? (
            <video
              src={previewUrl}
              poster={thumbUrl ?? undefined}
              controls
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover', background: '#1a1310' }}
            />
          ) : (
            <>
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  background:
                    'linear-gradient(135deg, oklch(0.7 0.1 45) 0%, oklch(0.55 0.12 35) 100%)',
                }}
              />
              <div className="caption-strip">{captionTeaser}</div>
              <div className="play-overlay">
                <div className="play-circ">
                  <Icon.Play size={26} />
                </div>
              </div>
              <div
                style={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  padding: '4px 10px',
                  borderRadius: 999,
                  background: 'oklch(0 0 0 / 0.5)',
                  color: 'white',
                  fontFamily: 'Heebo',
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                {durationLabel}
              </div>
              <div className="progress">
                <div className="fill" />
              </div>
            </>
          )}
        </div>

        <div
          className="card"
          style={{
            marginTop: 16,
            cursor: 'default',
            opacity: 0.72,
          }}
          aria-disabled
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: 'var(--surface)',
                color: 'var(--ink-mute)',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Icon.Sparkles size={20} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'Heebo', fontWeight: 600, fontSize: 15 }}>
                גרסה נוספת בחינם
              </div>
              <div style={{ fontSize: 13, color: 'var(--ink-mute)' }}>
                יתווסף בהמשך — כרגע תאורטי בלבד
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="cta-dock">
        <button className="btn-primary" onClick={onNext}>
          לרכישה והורדה
          <Icon.ArrowLeft size={18} />
        </button>
      </div>
    </div>
  );
}

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
